import React, {useEffect, useRef, useState} from 'react';
import { classNames } from '../../Services/helpers';
import { useAssistantContext } from './Provider';
import Markdown from '../Formatting/Markdown';
import Loading from '../Loading';
import ErrorBoundary from '../Containers/ErrorBoundary/ErrorBoundary';
import Animation from '../Animation';
import Entries from './Entries';
import ReplyBox from './ReplyBox';
import PreviousConversations, { PreviousConversationsHint } from './PreviousConversations';

const Content = ({showPreviousConversations = true}) => {

    return <div className="h-full w-full flex flex-col justify-between">
        <ErrorBoundary>
            <Messages {...{showPreviousConversations}} />
            <ReplyBox />
        </ErrorBoundary>
    </div>
}


const Messages = ({showPreviousConversations}) => {
    const {conversation, entries, files, assistantTyping, streamingAnswers, activeToolTitles, sendingQuestion, assistantOpen, submitMessage, welcomeMessage, previousConversations} = useAssistantContext();
    const scrollRef = useRef(null);
    const [userHasScrolled, setUserHasScrolled] = useState(false);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

    const scrollToBottom = () => {
        if (!scrollRef.current || !shouldAutoScroll) {
            return;
        }
        scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'instant',
        });
    };

    const handleScroll = () => {
        if (!scrollRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;

        setUserHasScrolled(true);
        setShouldAutoScroll(isAtBottom);
    };

    useEffect(() => {
        if (!userHasScrolled) {
            scrollToBottom();
        } else if (shouldAutoScroll) {
            scrollToBottom();
        }

        // Delayed scroll for content that might take time to render
        setTimeout(() => {
            if (!userHasScrolled || shouldAutoScroll) {
                scrollToBottom();
            }
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, streamingAnswers, sendingQuestion, assistantOpen, activeToolTitles, userHasScrolled, shouldAutoScroll, previousConversations]);

    const theEntries = [
        // Hide the 'Sid joined' if it's the first message
        ...entries.filter((e, i) => i !== 0 || e.type !== 'assistantActivated'),
        {role: 'User', message: sendingQuestion, sending: true}
    ];

    return <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={classNames(
            'w-full grow m-1 mt-0 px-1 pt-1 rounded-lg overflow-y-auto no-scrollbar',
            'text-sm text-wrap prose prose-sm',
        )}
    >
        {showPreviousConversations && <PreviousConversations />}

        <div className="flex flex-col gap-2 min-h-full">

            {showPreviousConversations && <PreviousConversationsHint />}

            {(!entries?.length && welcomeMessage) && <div className={classNames(
                            'max-w-[90%] p-2 rounded-lg text-balance',
                            'self-start left-left',
                            'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-bl-none'
                )}>
                    {welcomeMessage}
            </div>}

            <Entries entries={theEntries} submitMessage={submitMessage} files={files} />

            {assistantTyping && <div className={classNames(
                    'max-w-[90%] p-2 rounded-lg rounded-bl-none',
                    'self-start left-left',
                    'bg-slate-100 dark:bg-slate-700',
                )}>
                <Animation name="typing" />
            </div>}

            {streamingAnswers && streamingAnswers.map((streamingAnswer, index) => {
                if (!streamingAnswer || !streamingAnswer.trim()) {
                    return null;
                }
                return <div key={index} className={classNames(
                        'max-w-[90%] p-2 rounded-lg',
                        'self-start left-left',
                        'bg-gray-100 dark:bg-gray-800 text-slate-900 dark:text-slate-100',
                    )}>
                    <Markdown markdown={streamingAnswer} className="[&>:first-child]:mt-0 [&>:last-child]:mb-0" />
                </div>
            })}

            {activeToolTitles && activeToolTitles.map((activeToolTitle, index) => {
                return <div key={index}
                    className={classNames(
                        'flex items-center max-w-[90%]',
                        'py-2 px-4 rounded-lg',
                        'self-start left-left',
                        'bg-orange-100 dark:bg-orange-900 text-slate-900 dark:text-slate-100',
                    )}>
                    <Loading className="mr-2" />
                    {activeToolTitle}
                </div>
            })}

            {conversation?.assistantError && <div className="max-w-[90%] mx-auto text-center text-balance p-2 rounded-lg bg-red-50/50 text-red-600">
                {conversation.assistantError}
            </div>}

            <Status />

        </div>
    </div>
}

const Status = () => {
    const {conversation} = useAssistantContext();

    if (!conversation) {
        return null;
    }

    if (!conversation.assistantActive) {
        return <p className="mt-1 text-muted text-xs text-center">
            Sid has left, you're speaking to a human.
        </p>
    }

    return <p className="mt-1 text-muted text-xs text-center">
        Sid is still learning and might make mistakes.<br />
        {conversation.humanAttention ?
            'A human will join the conversation shortly.' :
            'You can ask to speak to a human at any time.'}
    </p>
}

export default Content;
